import { useNavigate } from 'react-router-dom';

import {
  Box,
  Button,
  Flex,
  Icon,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import { AddIcon, ChevronDownIcon } from '@chakra-ui/icons';

import { FaUsers } from 'react-icons/fa6';

import { CLIENT_CENTER_LIST_PATH } from '../../constants';

import AddRelatedContactModal from './AddRelatedContactModal';

import type { ActurisRelatedContact } from 'api/acturis/client-center';

interface RelatedContactsBadgeProps {
  customerId: string;
  relatedContacts: ActurisRelatedContact[];
  hasError: boolean;
}

const RelatedContactsBadge = ({
  customerId,
  relatedContacts,
  hasError,
}: RelatedContactsBadgeProps) => {
  const navigate = useNavigate();
  const labelColor = useColorModeValue('gray.500', 'gray.400');
  const textColor = useColorModeValue('gray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const activeBg = useColorModeValue('gray.100', 'gray.700');
  const addModal = useDisclosure();

  const handleView = (relatedContactRef: string) => {
    navigate(`/admin${CLIENT_CENTER_LIST_PATH}/${relatedContactRef}`);
  };

  return (
    <Popover placement="bottom-start">
      {({ isOpen, onClose }) => (
        <>
          <PopoverTrigger>
            <Button
              variant="ghost"
              size="xs"
              leftIcon={<Icon as={FaUsers} />}
              rightIcon={
                <ChevronDownIcon
                  transform={isOpen ? 'rotate(180deg)' : undefined}
                  transition="transform 0.2s"
                />
              }
              color={labelColor}
              bg={isOpen ? activeBg : undefined}
            >
              {hasError ? 'Related' : `${relatedContacts.length} Related`}
            </Button>
          </PopoverTrigger>
          <PopoverContent
            width="280px"
            _focus={{ boxShadow: 'md' }}
            _focusVisible={{ boxShadow: 'md' }}
          >
            <PopoverArrow />
            <PopoverBody p="0">
              {hasError && (
                <Text
                  color={labelColor}
                  fontSize="sm"
                  px="4"
                  py="2"
                  borderBottom="1px solid"
                  borderColor={borderColor}
                >
                  Couldn't load related contacts.
                </Text>
              )}
              {relatedContacts.map((relatedContact) => (
                <Flex
                  key={relatedContact.contact_related_contact_ref}
                  align="center"
                  justify="space-between"
                  gap="2"
                  px="4"
                  py="2"
                  borderBottom="1px solid"
                  borderColor={borderColor}
                >
                  <Box overflow="hidden">
                    <Text color={textColor} fontWeight="600" noOfLines={1}>
                      {relatedContact.name}
                    </Text>
                    <Text color={labelColor} fontSize="sm" noOfLines={1}>
                      {relatedContact.relationship_detail ||
                        relatedContact.relationship}
                    </Text>
                  </Box>
                  <Button
                    variant="link"
                    size="sm"
                    flexShrink={0}
                    onClick={() =>
                      handleView(relatedContact.related_contact_ref)
                    }
                  >
                    View
                  </Button>
                </Flex>
              ))}
              <Box px="2" py="2">
                <Button
                  variant="ghost"
                  size="sm"
                  width="100%"
                  justifyContent="flex-start"
                  leftIcon={<AddIcon boxSize="2.5" />}
                  onClick={() => {
                    onClose();
                    addModal.onOpen();
                  }}
                >
                  Add related contact
                </Button>
              </Box>
            </PopoverBody>
          </PopoverContent>
          <AddRelatedContactModal
            customerId={customerId}
            isOpen={addModal.isOpen}
            onClose={addModal.onClose}
          />
        </>
      )}
    </Popover>
  );
};

export default RelatedContactsBadge;
